import { Button } from '@civilgrid/ui/components';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { getAuthCode, login } from '~/api/auth2';
import { reportAuthError } from '~/common/reportAuthError';
import { emailRegex } from '~/components2/forms/common';
import Form from '~/components2/forms/Form';
import Input from '~/components2/forms/Input';
import authStore from '../../authStore';
import PageLayout from './PageLayout';
import store from './store';

interface LoginForm {
  email: string;
}

export default function LoginComponent() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { redirectUri } = useSnapshot(authStore);
  const [authCode, setAuthCode] = useState('');

  const { handleSubmit, control, formState } = useForm<LoginForm>({
    defaultValues: {
      email: '',
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (pathname.includes('authCode') && store.email === '') {
      navigate({ to: '/login' });
    }
  }, [pathname]);

  const onEmailSubmit = async ({ email }: LoginForm) => {
    try {
      store.email = email;
      const data = await getAuthCode(email, redirectUri);

      if (data.redirect) {
        if (data.redirect.startsWith('http')) {
          window.location.href = data.redirect;
        } else {
          navigate({ to: data.redirect });
        }
      } else {
        navigate({ to: '/login/authCode' });
      }
    } catch (e) {
      console.error(e);
      // Report on the auth page even without an authenticated user.
      reportAuthError(e, { context: 'auth:login:getAuthCode', step: 'getAuthCode' });
      alert('Oops, something went wrong. Please contact support if the issue persists.');
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const content = useMemo(() => {
    if (pathname.includes('authCode')) {
      return (
        <>
          <div className="my-2 text-sm">
            If {store.email} can sign in to CivilGrid, you will receive a login code within 5 minutes.
            <br />
            Wrong email?{' '}
            <Link to="/login" data-testid="login-go-back-link" className="text-blue-50 underline hover:text-blue-60">
              Go back.
            </Link>
          </div>
          <Form
            className="space-y-2"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                // Branch on the response shape (never the flag): a token means legacy bearer auth;
                // { email, userId } with no token means the server set the HttpOnly cookie and we
                // persist a session hint. TODO(INFRA-218): always the hint path once rolled out.
                const result = await login(store.email, authCode);
                if (result.token) {
                  authStore.setToken(result.token, redirectUri);
                } else if (result.email && result.userId) {
                  authStore.setSessionHint(result.email, result.userId, redirectUri);
                } else {
                  // Fail closed: an unrecognized response established no auth state, so don't redirect.
                  throw new Error('Login response missing both token and session hint');
                }

                navigate({ to: '/home' });
              } catch (e) {
                // A 401/403 is an expected wrong/expired code (user error) — don't flood Bugsnag.
                // Report everything else (network, 5xx, or the missing token/hint bug above).
                const status = axios.isAxiosError(e) ? e.response?.status : undefined;
                if (status !== 401 && status !== 403) {
                  reportAuthError(e, { context: 'auth:login:submitCode', step: 'submitCode' });
                }
                alert(
                  'Unable to login. Check your code and try again. If the issue persists, contact support@civilgrid.com',
                );
              }
            }}
          >
            <Input
              value={authCode}
              onChange={setAuthCode}
              label="Code"
              placeholder="123456"
              autoFocus
              data-testid="login-auth-code-input"
            />
            <Button type="submit" disabled={authCode.length !== 6} data-testid="login-auth-code-submit">
              Submit
            </Button>
          </Form>
        </>
      );
    } else {
      return (
        <>
          <Form onSubmit={handleSubmit(onEmailSubmit)} className="space-y-2">
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Please enter email',
                pattern: {
                  value: emailRegex,
                  message: 'Invalid email',
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  label="Email"
                  placeholder="alice@acme-engineers.com"
                  {...field}
                  error={error?.message}
                  autoFocus
                  data-testid="login-email-input"
                />
              )}
            />

            <Button type="submit" data-testid="login-submit-button">
              Login
            </Button>
          </Form>

          <div className="mt-2 text-xs">
            Don't have an account yet?{' '}
            <Link
              className="text-blue-50 underline hover:text-blue-60"
              to="/register"
              data-testid="login-register-link"
            >
              Register
            </Link>
          </div>
        </>
      );
    }
  }, [pathname, authCode, formState]);

  return <PageLayout data-testid="login-page">{content}</PageLayout>;
}
